import {t} from "@lingui/core/macro";
import {useMemo} from "react";

import type {Message} from "@khan/guide-types";
import {doNotTranslate} from "@khan/i18n";
import {useUserAttributes} from "@khan/shared-core";

import {useDynamicCoachMessage} from "./use-dynamic-coach-message.ts";

export function useGuideTermsMessages(): Message[] {
    const dynamicCoachMessage = useDynamicCoachMessage();
    const userAttributes = useUserAttributes();

    const isTeacher = userAttributes?.isTeacher || false;
    const isParent = userAttributes?.hasAccessToAIGuideParent || false;
    const isDistrictAdmin =
        userAttributes?.hasAccessToAIGuideDistrictAdmin || false;

    return useMemo(() => {
        if (isTeacher || isParent || isDistrictAdmin) {
            // NOTE(reid): For now, all of these personas see the teacher
            // messages, but we could revisit this someday.
            return [
                {
                    user: "Teacher",
                    text: t`Hi. I’m Khanmigo - your AI teaching assistant. I use Artificial Intelligence (AI) to help you teach and support your students in reaching their learning goals.`,
                    type: "incoming",
                    serverOnly: false,
                    uiOnly: false,
                    interactionId: "",
                },
                {
                    user: "Teacher",
                    text: t`I’m still pretty new, so I sometimes make mistakes. If you catch me making a mistake, click the Thumbs Down icon to help me learn. For example, if I give you the wrong math answer, click Thumbs Down.`,
                    type: "incoming",
                    serverOnly: false,
                    uiOnly: false,
                    interactionId: "",
                },
                {
                    user: "Teacher",
                    text: t`A few tips on using me safely:
- Please do not include any personal details in your messages to me.
- If you have students using Khanmigo, use chat history to review their interactions with me.`,
                    type: "incoming",
                    serverOnly: false,
                    uiOnly: false,
                    interactionId: "",
                },
                {
                    user: "Teacher",
                    text: t`Please click on "I understand" to start working together.`,
                    type: "incoming",
                    serverOnly: false,
                    uiOnly: false,
                    interactionId: "",
                    actionBubbles: [
                        {
                            label: t`I understand`,
                            command: t`I understand`,
                            oldMessageBehavior: "hide",
                            actions: [],
                        },
                    ],
                },
            ];
        }

        const CHAT_BOT_NAME: string = doNotTranslate`Khanmigo`;

        // By default, return the learner-facing messages:
        return [
            {
                text: t`Hi. I’m ${CHAT_BOT_NAME} - your AI guide. I use Artificial Intelligence (AI) to help you learn on Khan Academy. It’s my favorite thing!` as string,
                user: "Teacher",
                type: "incoming",
                serverOnly: false,
                uiOnly: false,
                interactionId: "",
            },
            {
                text: t`I'm still pretty new, so I sometimes make mistakes. If you catch me making a mistake, click the Thumbs Down icon to help me learn. For example, if I give you the wrong math answer, click Thumbs Down.` as string,
                user: "Teacher",
                type: "incoming",
                serverOnly: false,
                uiOnly: false,
                interactionId: "",
            },
            dynamicCoachMessage,
            {
                text: t`Please click on "I understand" to start learning today.` as string,
                user: "Teacher",
                type: "incoming",
                serverOnly: false,
                uiOnly: false,
                interactionId: "",
                actionBubbles: [
                    {
                        label: t`I understand`,
                        command: t`I understand`,
                        oldMessageBehavior: "hide",
                        actions: [],
                    },
                ],
            },
        ];
    }, [isTeacher, isParent, isDistrictAdmin, dynamicCoachMessage]);
}
